import L from "leaflet";
import { useEffect, useMemo } from "react";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";

import type { CityLocation, LayerKey, StormPoint, StormTrack } from "../domain/types";
import {
  buildQuadrantWindPolygon,
  calculateMovementSpeedKph,
  classifyTyphoonByWindSpeed,
  interpolateStormPoint,
  resolvePublicAssetUrl,
  windSpeedKphToBeaufort,
} from "../map/trackMath";

const layerStyle = {
  observed: { color: "#343230", weight: 3, opacity: 0.9 },
  forecast: { color: "#ef6b18", weight: 2.5, opacity: 0.95, dashArray: "7 7" },
  wind7: { color: "#eca349", fillColor: "#f6c983", fillOpacity: 0.14, weight: 1 },
  wind10: { color: "#ef7b2c", fillColor: "#efa458", fillOpacity: 0.12, weight: 1.15 },
  wind12: { color: "#56514c", fillColor: "#7c7770", fillOpacity: 0.08, weight: 1.2 },
} as const;

const stormIcon = L.divIcon({
  className: "storm-marker-shell",
  html: `<img alt="" class="storm-marker-image" draggable="false" src="${resolvePublicAssetUrl("typhoon-center.png")}" />`,
  iconAnchor: [15, 15],
  iconSize: [30, 30],
});

const cityIcon = (selected: boolean) => L.divIcon({
  className: "city-marker-shell",
  html: `<span class="city-marker${selected ? " city-marker--selected" : ""}"></span>`,
  iconAnchor: [6, 6],
  iconSize: [12, 12],
});

function getTimelineStart(track: StormTrack) {
  const latestObserved = [...track.observed].sort((a, b) => Date.parse(b.time) - Date.parse(a.time))[0];
  return latestObserved?.time ?? track.forecast[0]?.time ?? track.updatedAt;
}

function trackPositions(points: StormPoint[]) {
  return points.map((point) => [point.latitude, point.longitude] as [number, number]);
}

function MapViewport({ track, selectedCity }: { track: StormTrack; selectedCity: CityLocation | null }) {
  const map = useMap();
  useEffect(() => {
    map.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');
    window.setTimeout(() => map.invalidateSize({ pan: false }), 0);
  }, [map]);
  useEffect(() => {
    const recent = [...track.observed.slice(-5), ...track.forecast];
    const positions = trackPositions(recent);
    if (selectedCity) {
      positions.unshift([selectedCity.latitude, selectedCity.longitude]);
    }
    if (positions.length > 1) {
      map.fitBounds(positions, { padding: [42, 42], maxZoom: 6.8, animate: false });
    } else if (selectedCity) {
      map.flyTo([selectedCity.latitude, selectedCity.longitude], 6.8, { duration: 0.5 });
    }
  }, [map, selectedCity, track]);
  return null;
}

interface TyphoonMapProps {
  track: StormTrack;
  currentHour: number;
  pinnedCities: CityLocation[];
  selectedCity: CityLocation | null;
  hiddenLayers: LayerKey[];
  startTime?: string;
  tiandituToken: string;
}

export function TyphoonMap({
  track,
  currentHour,
  pinnedCities,
  selectedCity,
  hiddenLayers,
  startTime,
  tiandituToken,
}: TyphoonMapProps) {
  const hidden = useMemo(() => new Set(hiddenLayers), [hiddenLayers]);
  const targetTime = useMemo(() => {
    const start = Date.parse(startTime ?? getTimelineStart(track));
    return new Date(start + currentHour * 3_600_000).toISOString();
  }, [currentHour, startTime, track]);
  const allPoints = useMemo(
    () => [...track.observed, ...track.forecast].sort((a, b) => Date.parse(a.time) - Date.parse(b.time)),
    [track],
  );
  const currentPoint = useMemo(
    () => interpolateStormPoint(allPoints, targetTime, track.windRadiiTimeline),
    [allPoints, targetTime, track.windRadiiTimeline],
  );
  const movementSpeedKph = useMemo(
    () => calculateMovementSpeedKph(allPoints, targetTime),
    [allPoints, targetTime],
  );
  const windScale = windSpeedKphToBeaufort(currentPoint?.windSpeedKph ?? null);
  const typhoonCategory = classifyTyphoonByWindSpeed(currentPoint?.windSpeedKph ?? null);
  const positionText = currentPoint
    ? `${Math.abs(currentPoint.latitude).toFixed(1)}°${currentPoint.latitude >= 0 ? "N" : "S"} / ${Math.abs(currentPoint.longitude).toFixed(1)}°${currentPoint.longitude >= 0 ? "E" : "W"}`
    : "—";
  const windText = currentPoint?.windSpeedKph === null || !currentPoint
    ? "—"
    : `${Math.round(currentPoint.windSpeedKph)} km/h${windScale === null ? "" : ` · ${windScale}级`}`;
  const movementText = movementSpeedKph === null ? "—" : `${Math.round(movementSpeedKph)} km/h`;
  const currentLabel = currentPoint
    ? `当前台风中心：${typhoonCategory}，中心位置 ${positionText}，风速风力 ${windText}，移动速度 ${movementText}`
    : "当前暂无台风中心信息";

  return (
    <div className="typhoon-map-frame">
      <span aria-label={currentLabel} className="sr-only" role="status" />
      <MapContainer
        attributionControl
        center={[28, 123]}
        className="typhoon-map"
        maxZoom={12}
        minZoom={3}
        preferCanvas
        scrollWheelZoom
        zoom={5.6}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.tianditu.gov.cn/">天地图</a>'
          className="map-tiles--grayscale"
          maxZoom={18}
          subdomains={["0", "1", "2", "3", "4", "5", "6", "7"]}
          url={`https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}&tk=${encodeURIComponent(tiandituToken)}`}
        />
        <TileLayer
          className="map-tiles--grayscale map-labels"
          maxZoom={18}
          pane="overlayPane"
          subdomains={["0", "1", "2", "3", "4", "5", "6", "7"]}
          url={`https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}&tk=${encodeURIComponent(tiandituToken)}`}
        />

        {currentPoint && !hidden.has("rain") && currentPoint.windRadii[0] ? (
          <Polygon
            className="rain-impact-envelope"
            interactive={false}
            pathOptions={{ color: "transparent", fillColor: "#efac56", fillOpacity: 0.05 }}
            positions={buildQuadrantWindPolygon(
              currentPoint.latitude,
              currentPoint.longitude,
              currentPoint.windRadii[0],
              16,
            ).map((point) => [point.latitude, point.longitude])}
          />
        ) : null}

        {currentPoint?.windRadii.map((radii) => {
          const layer = radii.windScale === 7 ? "wind7" : radii.windScale === 10 ? "wind10" : "wind12";
          if (hidden.has(layer)) return null;
          return (
            <Polygon
              className="wind-envelope"
              interactive={false}
              key={`${radii.thresholdKt}-${radii.evidence}`}
              pathOptions={{
                ...layerStyle[layer],
                dashArray: radii.evidence === "observed" ? undefined : "5 6",
              }}
              positions={buildQuadrantWindPolygon(currentPoint.latitude, currentPoint.longitude, radii, 16)
                .map((point) => [point.latitude, point.longitude])}
            />
          );
        })}

        {!hidden.has("observed") && track.observed.length > 1 ? (
          <Polyline interactive={false} pathOptions={layerStyle.observed} positions={trackPositions(track.observed)} />
        ) : null}
        {!hidden.has("forecast") && track.forecast.length ? (
          <Polyline
            interactive={false}
            pathOptions={layerStyle.forecast}
            positions={trackPositions([track.observed.at(-1), ...track.forecast].filter((point): point is StormPoint => Boolean(point)))}
          />
        ) : null}

        {[...track.observed, ...track.forecast].map((point) => (
          <CircleMarker
            center={[point.latitude, point.longitude]}
            key={point.id}
            pathOptions={{
              color: point.evidence === "observed" ? "#343230" : "#ef6b18",
              fillColor: point.evidence === "observed" ? "#343230" : "#ffffff",
              fillOpacity: 1,
              weight: 1.5,
            }}
            radius={point.evidence === "observed" ? 3.4 : 4.4}
          >
            <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
              {new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(point.time))}
            </Tooltip>
          </CircleMarker>
        ))}

        {currentPoint ? (
          <Marker icon={stormIcon} position={[currentPoint.latitude, currentPoint.longitude]} zIndexOffset={0}>
            <Tooltip className="storm-tooltip" direction="right" offset={[18, 0]} permanent>
              <strong>{typhoonCategory}</strong>
              <span>中心位置 {positionText}</span>
              <span>风速风力 {windText}</span>
              <span>移动速度 {movementText}</span>
            </Tooltip>
          </Marker>
        ) : null}

        {!hidden.has("cities") ? pinnedCities.map((city) => (
          <Marker
            icon={cityIcon(selectedCity?.id === city.id)}
            key={city.id}
            position={[city.latitude, city.longitude]}
            zIndexOffset={2000}
          >
            <Tooltip className="city-tooltip" direction="top" offset={[0, -7]} permanent>
              {city.name}
            </Tooltip>
          </Marker>
        )) : null}
        <MapViewport selectedCity={selectedCity} track={track} />
      </MapContainer>
      {!tiandituToken ? (
        <div className="map-token-notice glass-panel" role="status">天地图 Token 未配置，路径数据仍可查看</div>
      ) : null}
      <div className="map-legend glass-panel" aria-label="地图图例">
        <span><i className="legend-line legend-line--observed" />实况</span>
        <span><i className="legend-line legend-line--forecast" />预测</span>
        <span><i className="legend-line legend-line--extrapolated" />风圈</span>
        <span><i className="legend-line legend-line--insufficient" />暂无风圈</span>
      </div>
    </div>
  );
}
